import { merchantKey } from './recurring.ts';
import type { TransactionKind } from './types.ts';

// Categories learned from the owner's own history: every categorised transaction is a vote for
// its merchant. No separate rule list to maintain — correcting a category is what teaches it.

export type CategorizedTransaction = {
  readonly id: string;
  readonly merchant: string | null;
  readonly description: string | null;
  readonly kind: TransactionKind;
  readonly categoryId: string | null;
};

export type CategorySuggestion = {
  readonly categoryId: string;
  /** How many of this merchant's categorised transactions use it. */
  readonly votes: number;
  /** Share of the votes, 0–10 000. */
  readonly share: number;
};

const LEARNS = new Set<TransactionKind>(['EXPENSE', 'INCOME', 'REFUND']);

const keyOf = (t: Pick<CategorizedTransaction, 'merchant' | 'description'>) => merchantKey(t.merchant ?? t.description ?? '');

/**
 * Builds the merchant → category memory. A merchant is suggested a category when at least
 * two of its transactions agree and they are a clear majority (≥ 60 %).
 */
export function learnCategories(transactions: readonly CategorizedTransaction[]): Map<string, CategorySuggestion> {
  const votes = new Map<string, Map<string, number>>();
  for (const t of transactions) {
    if (!t.categoryId || !LEARNS.has(t.kind)) continue;
    const key = keyOf(t);
    if (!key) continue;
    const byCategory = votes.get(key) ?? new Map<string, number>();
    byCategory.set(t.categoryId, (byCategory.get(t.categoryId) ?? 0) + 1);
    votes.set(key, byCategory);
  }
  const memory = new Map<string, CategorySuggestion>();
  for (const [key, byCategory] of votes) {
    const total = [...byCategory.values()].reduce((a, b) => a + b, 0);
    const [categoryId, count] = [...byCategory.entries()].sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))[0]!;
    const share = Math.floor((count * 10_000) / total);
    if (count >= 2 && share >= 6000) memory.set(key, { categoryId, votes: count, share });
  }
  return memory;
}

/**
 * The suggested category for an uncategorised transaction, or null. A bank text cut short (Lloyds
 * statements end at 18 characters: "LEBARA MOBILE LIMI") may end in half a word: then the merchant
 * without that word, or the only known merchant the cut name begins.
 */
export function suggestCategory(memory: ReadonlyMap<string, CategorySuggestion>, t: CategorizedTransaction, options: { cut?: boolean } = {}): CategorySuggestion | null {
  if (t.categoryId || !LEARNS.has(t.kind)) return null;
  const key = keyOf(t);
  const exact = memory.get(key);
  if (exact || !options.cut || !key) return exact ?? null;
  const words = key.split(' ');
  const shorter = words.length > 1 ? memory.get(words.slice(0, -1).join(' ')) : undefined;
  if (shorter) return shorter;
  const longer = [...memory.keys()].filter((k) => k.startsWith(key));
  return longer.length === 1 ? memory.get(longer[0]!)! : null;
}

/** Other transactions of the same merchant whose category differs from `categoryId`. */
export function sameMerchant<T extends CategorizedTransaction>(transactions: readonly T[], of: CategorizedTransaction, categoryId: string | null): T[] {
  const key = keyOf(of);
  if (!key) return [];
  return transactions.filter((t) => t.id !== of.id && LEARNS.has(t.kind) && t.categoryId !== categoryId && keyOf(t) === key);
}
